<template>
  <div class="modal">
    <div>
      <div class="container">
        <div class="close-button" @click="emits('onClose', false)"></div>
        <div v-if="!!error">
          <p class="title">Ошибка отправки смс</p>
          <p class="description">
            {{ error }}
          </p>
        </div>

        <div v-else-if="!isSmsFormCompleted">
          <div class="close-button" @click="emits('onClose', false)"></div>

          <p class="title">Введите код из СМС</p>

          <BaseOtpInput
            test-id="sms-code"
            separator=""
            inputType="number"
            :num-inputs="6"
            :should-auto-focus="true"
            @on-change="onSmsFormChange"
            @on-complete="onSmsFormComplete"
            :is-disabled="!isNewSmsSendedAfterError"
          />

          <p class="description">
            Код отправлен на номер +7 (***) *** -{{ phoneNumberPart }}
          </p>

          <!-- TODO: option when timer is over -->
          <p v-if="time !== 0" class="description">
            Запросить повторно можно через
            <span class="timer">00:{{ timeLastToString(time) }}</span>
          </p>

          <p v-else @click="onResendSms" class="resend">
            Отправить код ещё раз
          </p>

          <div v-if="hasError">
            <BaseAlert v-if="props.errorCode === 'CONFIRM/INVALID'">
              {{ 'Неправильно введен код подтверждения.' }}
              {{ `Осталось попыток: ${props.checkCount}` }}
            </BaseAlert>

            <BaseAlert v-else-if="props.errorCode === 'CONFIRM/CHECK_COUNT'">
              {{ 'Исчерпано количество попыток' }}
            </BaseAlert>

            <BaseAlert v-else-if="props.errorCode">
              {{ 'Что-то пошло не так' }}
            </BaseAlert>
          </div>
        </div>
        <div v-else>
          <p class="title">Пожалуйста, подождите</p>

          <div class="spinner">
            <BaseSpinner :show="true" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/* ---- Funcs ---- */
import { timeLastToString } from '@/helpers/timeLastToString';
import { useSmsTimer } from '@/composables/useSmsTimer';
import { useSmsForm } from '@/composables/useSmsForm';

/* ---- Components ---- */
import BaseOtpInput from '@/components/BaseOtpInput.vue';
import BaseSpinner from '@/components/BaseSpinner.vue';
import BaseAlert from '@/components/BaseAlert.vue';
import { computed, onMounted, ref } from 'vue';
import { useApiConnectAuthorize } from '@/composables/useApiConnectAuthorize';
import { postSmsAuthentication } from '@/api/personStsServiceApi';
import { phoneNumberMask } from '@/helpers/phoneNumberMask';

/* ---- props ---- */
const props = defineProps([
  'phoneNumber',
  'clientId',
  'redirectUri',
  'codeChallenge',
  'isAuthFormCompleted',
  'errorCode',
  'errorMessage',
  'checkCount',
  'expires',
]);

/* ---- emits ---- */
const emits = defineEmits<{
  (e: 'onClose', open: boolean): void;
}>();

/* ---- computed ---- */
const clientId = computed(() => props.clientId);
const redirectUri = computed(() => props.redirectUri);
const phoneNumber = computed(() => props.phoneNumber);
const phoneNumberPart = computed(
  () =>
    `${props.phoneNumber.slice(-4, -2)}-${props.phoneNumber.slice(
      -2,
      props.phoneNumber.length
    )}`
);
const codeChallenge = computed(() => props.codeChallenge);

/* ---- state ---- */
const isCodeResended = ref(false);
const error = ref();
const isSmsAuthSucceed = ref(false);
const hasError = ref();

/* ---- composables ---- */
const { resetTimer, time } = useSmsTimer(isSmsAuthSucceed);
const { isSmsFormCompleted, onSmsFormChange, onSmsFormComplete, smsCode } =
  useSmsForm();
const { isSmsDataCatched, data, smsError } = useApiConnectAuthorize(
  isSmsFormCompleted,
  clientId,
  redirectUri,
  smsCode,
  phoneNumber,
  codeChallenge
);
const isNewSmsSendedAfterError = ref(true);

/* ---- hooks ---- */
onMounted(async () => {
  if (!props.errorCode) {
    await postSmsAuth(props.phoneNumber);
  } else if (
    props.errorCode === 'CONFIRM/CHECK_COUNT' ||
    props.errorCode === 'CONFIRM/NOT_FOUND'
  ) {
    hasError.value = true;
    isNewSmsSendedAfterError.value = false;
  } else {
    hasError.value = true;
  }
  isSmsAuthSucceed.value = true;
});

/* ---- handlers ---- */
const onResendSms = async () => {
  hasError.value = false;
  isSmsAuthSucceed.value = false;
  resetTimer(isCodeResended.value);
  await postSmsAuth(props.phoneNumber);
  isSmsAuthSucceed.value = true;
  isNewSmsSendedAfterError.value = true;
};

/* ---- methods ---- */
const postSmsAuth = async (phoneNumber: string) => {
  await postSmsAuthentication(phoneNumberMask(phoneNumber))
    .then((_response) => {
      isSmsAuthSucceed.value = true;
    })
    .catch((_error) => {
      error.value = _error;
    });
};
</script>

<style lang="scss" scoped>
.modal {
  position: fixed;
  z-index: 999;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(39, 48, 62, 0.6);
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.container {
  background-color: white;
  position: relative;
  padding: 48px;
  height: fit-content;
  width: 632px;
  border-radius: 24px;
  box-shadow: 0 6px 26px rgba($color: #000000, $alpha: 0.1);
}

.title {
  font-size: 24px;
  font-weight: 500;
  width: 100%;
  text-align: center;
  margin-top: 0;
  margin-bottom: 48px;
}

.field {
  margin-bottom: 24px;
}

.spinner {
  display: flex;
  justify-content: center;
}

.description {
  font-weight: 300;
  font-size: 16px;
  width: 100%;
  text-align: center;
  color: $gray-primary;
  margin-top: 0;
  margin-bottom: 8px;
}

.timer {
  color: $blue;
}

.resend {
  font-weight: 300;
  font-size: 16px;
  width: 100%;
  text-align: center;
  margin-top: 0;
  margin-bottom: 8px;
  color: $blue;
  cursor: pointer;
}

.close-button {
  width: 48px;
  height: 48px;
  background-color: white;
  position: absolute;
  right: -48px;
  top: -48px;
  border-radius: 100%;
  background-image: url(../assets/close.svg);
  background-position: center;
  background-repeat: no-repeat;
  box-shadow: 0 6px 26px rgba($color: #000000, $alpha: 0.1);
  cursor: pointer;
  transition: all cubic-bezier(0.4, 0, 0.2, 1) 150ms;

  &:hover {
    background-color: $lightblue;
  }

  &:active {
    transform: scale(0.9);
  }
}

@media only screen and (max-width: 768px) {
  /* For mobile phones: */
  .container {
    background-color: white;
    position: relative;
    padding: 32px 20px 32px 20px;
    height: fit-content;
    margin-left: 16px;
    margin-right: 16px;
    width: auto;
  }

  .title {
    font-size: 20px;
    margin-bottom: 24px;
  }

  .description {
    font-weight: 400;
    font-size: 12px;
  }

  .resend {
    font-weight: 400;
    font-size: 12px;
  }

  .close-button {
    right: 0px;
    top: -60px;
  }
}
</style>
